import { parseFormData } from "@mjackson/form-data-parser";
import { type SEOHandle } from "@nasa-gcn/remix-seo";
import * as E from "@react-email/components";
import {
  type ActionFunctionArgs,
  type HeadersFunction,
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
  redirect,
  type SerializeFrom,
} from "@remix-run/node";
import {
  Link,
  useFetcher,
  useLoaderData,
  useLocation,
  useNavigate,
} from "@remix-run/react";
import { ptBR } from "date-fns/locale";
import { toZonedTime, format } from "date-fns-tz";
import { isbot } from "isbot";
import DOMPurify from "isomorphic-dompurify";
import {
  Building,
  Clock,
  DollarSign,
  Loader2,
  MapPin,
  Pencil,
  Phone,
  Star,
  StarOff,
  ThumbsDown,
  ThumbsUp,
  ChevronDown,
  Minus,
  Send,
  Upload,
  Map,
  PlusCircle,
  ArrowLeft,
  Share2,
  Zap,
  User2,
  ChevronRight,
  MessageCircle,
  CircleUserRound,
} from "lucide-react";
import * as React from "react";
import { HoneypotInputs } from "remix-utils/honeypot/react";
import { toast } from "sonner";
import { serverOnly$ } from "vite-env-only/macros";
import AdPlaceholder from "#app/components/ad-placeholder.js";
import { GeneralErrorBoundary } from "#app/components/error-boundary.tsx";
import Ratings from "#app/components/ratings.js";
import { ActionButtons } from "#app/components/station/action-buttons.tsx";
import { ConnectorList } from "#app/components/station/connector-list.tsx";
import { ImageGallery } from "#app/components/station/image-gallery.tsx";
import { ReviewsSection, type Review } from "#app/components/station/reviews-section.tsx";
import { StationDescription } from "#app/components/station/station-description.tsx";
import {
  getStatusText,
  getStatusVariant,
  StationHeader,
} from "#app/components/station/station-header.tsx";
import { StationInfo } from "#app/components/station/station-info.tsx";
import { ThreadMessageForm } from "#app/components/threads/thread-message-form.tsx";
import { ThreadMessages } from "#app/components/threads/thread-messages.tsx";
import { ThreadReplyCount } from "#app/components/threads/thread-reply-count.tsx";
import { Badge } from "#app/components/ui/badge.tsx";
import { Button, buttonVariants } from "#app/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "#app/components/ui/dialog.tsx";
import { Icon } from "#app/components/ui/icon.tsx";
import { Input } from "#app/components/ui/input.tsx";
import { Label } from "#app/components/ui/label.tsx";
import LastInteraction from "#app/components/ui/last-interaction.tsx";
import { RadioGroup, RadioGroupItem } from "#app/components/ui/radio-group.tsx";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "#app/components/ui/sheet.tsx";
import Speed from "#app/components/ui/speed.js";
import { Textarea } from "#app/components/ui/textarea.tsx";
import { getUserId } from "#app/utils/auth.server.ts";
import { getConnectorInfo, getNetworkName } from "#app/utils/connectors.ts";
import { adPreferencesCookie } from "#app/utils/cookies.server.js";
import { prisma } from "#app/utils/db.server.ts";
import { sendEmail } from "#app/utils/email.server.ts";
import { formatState } from "#app/utils/format-text.tsx";
import { checkHoneypot } from "#app/utils/honeypot.server.ts";
import {
  cn,
  getMapImgSrc,
  getStationImgSrc,
} from "#app/utils/misc.tsx";
import { uploadStationImage } from "#app/utils/storage.server.ts";
import { makeTimings, time } from "#app/utils/timing.server.ts";
import { useOptionalUser } from "#app/utils/user.ts";
import { locationViewsCookie } from "#app/utils/views-cookie.server.ts";

const MAX_UPLOAD_SIZE = 20 * 1024 * 1024; // 20 mb

type ReviewActionData = {
  status: number;
  message: string;
  newReview: {
    reviews: Array<Review>; // Use the Review interface
  };
  rating: string;
};

export async function action({ request }: ActionFunctionArgs) {
  const formData = await parseFormData(request, {
    maxFileSize: MAX_UPLOAD_SIZE,
  });

  checkHoneypot(formData);

  const locationId = formData.get("locationId");
  const userId = formData.get("userId");
  const comment = formData.get("comment");
  const rating = formData.get("rating");
  const active = formData.get("active");
  const kilowatts = formData.get("kilowatts");
  const content = formData.get("content");
  const reviewId = formData.get("reviewId");
  const imageId = formData.get("imageId");

  let files = formData.getAll("file") as any;
  files = await Promise.all(
    files.map(async (file: any) => {
      if (file.size <= 0 || file.size > MAX_UPLOAD_SIZE) {
        return json(
          { message: "O tamanho da imagem deve ser menor que 20MB" } as const,
          { status: 500 },
        );
      }
      return {
        blob: Buffer.from(await file.arrayBuffer()),
        contentType: file.type,
        file,
      };
    }),
  );

  const fileData = files[0];

  const intent = formData.get("intent");

  if (!rating && intent === "review") {
    return json({
      message:
        "Selecione a avaliação entre positiva 👍, neutra ➖ ou negativa 👎.",
      status: 500,
    } as const);
  }

  switch (intent) {
    case "delete-review":
      if (!reviewId || !userId) {
        return json({
          message: "Dados insuficientes para excluir a avaliação",
          status: 500,
        } as const);
      }

      // Verify that the user owns the review
      const reviewToDelete = await prisma.review.findUnique({
        where: { id: Number(reviewId) },
        select: {
          userId: true,
          rating: true,
          locationId: true,
        },
      });

      if (!reviewToDelete) {
        return json({
          message: "Avaliação não encontrada",
          status: 404,
        } as const);
      }

      if (reviewToDelete.userId !== userId) {
        return json({
          message: "Você não tem permissão para excluir esta avaliação",
          status: 403,
        } as const);
      }

      // Update location stats based on the review rating
      const locationUpdate: Record<string, any> = {
        totalReviews: { decrement: 1 },
      };

      if (reviewToDelete.rating === 1) {
        locationUpdate.positives = { decrement: 1 };
      } else if (reviewToDelete.rating === 0) {
        locationUpdate.negatives = { decrement: 1 };
      }

      // Delete the review and update location stats in a transaction
      await prisma.$transaction([
        prisma.review.delete({
          where: { id: Number(reviewId) },
        }),
        prisma.location.update({
          where: { id: reviewToDelete.locationId },
          data: locationUpdate,
        }),
        prisma.user.update({
          where: { id: userId as string },
          data: {
            score: { decrement: 2 },
          },
        }),
      ]);

      return json({
        message: "Avaliação excluída com sucesso",
        status: 200,
      } as const);

    case "review":
      if (rating === "2" && !comment) {
        return json({
          message:
            "Para avaliações neutras, é obrigatório fornecer um comentário.",
          status: 500,
        } as const);
      }

      //find if user already reviewed today, i will allow only one review per dai
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const review = await prisma.review.findFirst({
        where: {
          locationId: Number(locationId),
          userId: userId as string,
        },
        orderBy: {
          createdAt: "desc",
        },
        select: {
          createdAt: true,
        },
      });

      if (review && review.createdAt > today) {
        return json({
          message: "Você já avaliou essa estação hoje.",
          status: 500,
        } as const);
      }

      const mainUserCar = await prisma.userUserCar.findFirst({
        where: {
          userId: userId as string,
          isMain: true,
        },
        include: {
          userCar: {
            select: {
              name: true,
              make: true,
            },
          },
        },
      });

      let vehicleName;
      if (mainUserCar) {
        vehicleName = mainUserCar.userCar.name;
        if (mainUserCar.subtypeName && mainUserCar.subtypeName !== "none") {
          vehicleName += ` ${mainUserCar.subtypeName}`;
        }
      }

      const newReview = await prisma.location.update({
        where: { id: Number(locationId) },
        data: {
          reviews: {
            create: {
              comment: comment as string,
              userId: userId as string,
              rating: Number(rating),
              kilowatts: kilowatts ? Number(kilowatts) : undefined,
              vehicleName: vehicleName ? vehicleName : undefined,
              vehicleMake: mainUserCar?.userCar.make,
            },
          },
          totalReviews: {
            increment: 1,
          },
          positives: rating == "1" ? { increment: 1 } : undefined,
          negatives: rating == "0" ? { increment: 1 } : undefined,
        },
        include: {
          reviews: {
            take: 1,
            orderBy: {
              createdAt: "desc",
            },
            include: {
              user: {
                select: {
                  id: true,
                  username: true,
                  score: true,
                  image: {
                    select: {
                      id: true,
                      objectKey: true,
                    },
                  },
                },
              },
            },
          },
        },
      });

      await prisma.user.update({
        where: {
          id: userId as string,
        },
        data: {
          score: {
            increment: 2,
          },
        },
      });

      return json({ message: "", newReview, rating, status: 200 } as const);

    case "image":
      // Upload image and get object key + content type
      const { objectKey, contentType } = await uploadStationImage(
        Number(locationId),
        fileData.file,
      );

      // Assuming you have a StationImage model
      const newImage = await prisma.stationImage.create({
        data: {
          locationId: Number(locationId),
          userId: userId as string,
          contentType: contentType, // Use returned contentType
          objectKey: objectKey, // Use returned objectKey
        },
      });

      await prisma.user.update({
        where: {
          id: userId as string,
        },
        data: {
          score: {
            increment: 5,
          },
        },
      });

      const newImageWithUser = await prisma.stationImage.findUnique({
        where: { id: newImage.id },
        select: {
          id: true,
          contentType: true,
          objectKey: true,
          createdAt: true,
          userId: true,
          locationId: true,
        },
      });
      return json({
        message: "",
        newImage: newImageWithUser,
        status: 200,
      } as const);

    case "delete-image":
      if (!imageId || !userId) {
        return json({
          message: "Dados insuficientes para excluir a imagem",
          status: 500,
        } as const);
      }

      // Verify ownership
      const imageToDelete = await prisma.stationImage.findUnique({
        where: { id: imageId as string },
        select: { userId: true },
      });

      if (!imageToDelete) {
        return json({ message: "Imagem não encontrada", status: 404 } as const);
      }

      if (imageToDelete.userId !== userId) {
        return json({
          message: "Você não tem permissão para excluir esta imagem",
          status: 403,
        } as const);
      }

      // Delete the image record and update user score
      try {
        await prisma.$transaction([
          prisma.stationImage.delete({ where: { id: imageId as string } }),
          prisma.user.update({
            where: { id: userId as string },
            data: {
              score: { decrement: 5 }, // Decrement score
            },
          }),
        ]);

        return json({
          message: "Imagem excluída com sucesso",
          status: 200,
        } as const);
      } catch (error) {
        console.error("Error deleting image:", error);
        return json({
          message: "Erro ao excluir a imagem do banco de dados",
          status: 500,
        } as const);
      }

    case "favorite":
      if (!userId) {
        return json({ message: "Usuário não encontrado" } as const, {
          status: 500,
        });
      }

      const favorite = await prisma.favoriteStation.findFirst({
        where: {
          userId: userId as string,
          locationId: Number(locationId),
        },
      });

      if (favorite) {
        await prisma.favoriteStation.delete({
          where: {
            id: favorite.id,
          },
        });

        return json({ message: "Estação removida dos favoritos" } as const, {
          status: 200,
        });
      }

      const newStation = await prisma.favoriteStation.create({
        data: {
          userId: userId as string,
          locationId: Number(locationId),
        },
      });

      return json({ message: "", newStation } as const, { status: 200 });

    case "info":
      const response = await sendEmail({
        to: '"Carregados" <contato@carregados.com.br>',
        subject: `Ateração de informações na estação ${
          locationId?.toString() || ""
        }`,
        react: (
          <E.Html lang="en" dir="ltr">
            <E.Container>
              <h1>
                <E.Text>
                  O usuário {userId?.toString() || ""} solicitou uma alteração.
                </E.Text>
              </h1>
              <p>
                <E.Text>Ativo: {active?.toString() || ""}</E.Text>
              </p>
              <p>
                <E.Text>Conteúdo: {content?.toString() || ""}</E.Text>
              </p>
            </E.Container>
          </E.Html>
        ),
      });

      if (response.status === "success") {
        return json({ message: "success" } as const, { status: 200 });
      } else {
        return json({ message: "error" } as const, { status: 500 });
      }
    default:
      return json({ message: "Invalid intent", status: 400 } as const);
  }
}

type Station = {
  id: number;
  name: string;
  url: string;
  objectKey: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  address: string | null;
  description: string | null;
  hours: string | null;
  parkingLevel: string | null;
  positives: number;
  negatives: number;
  averagePower: number | null;
  maxReportedPower: number | null;
  formattedPhoneNumber: string | null;
  costDescription: string | null;
  hasMap: boolean;
  majorityNetworkId: number | null;
  icon_type: string;
  isInactive: boolean;
  reviews: Review[];
  stations: Array<{
    id: number;
    networkId: number | null;
    outlets: Array<{
      id: number;
      connectorName: string | null;
      connector: string | null;
      kilowatts: number | null;
    }>;
  }>;
  tags: Array<{
    name: string;
  }>;
  reverseGeocodedAddressComponents: Array<{
    administrativeArea1: string | null;
    administrativeArea2: string | null;
  }>;
  stationImages: Array<{
    id: string;
    objectKey: string;
    createdAt: string;
    userId: string | null;
    user: {
      username: string | null;
    };
  }>;
};

type LoaderData = {
  station: (Omit<Station, 'reviews'> & { reviews: Review[] }) | null;
  lastUserReviews: any[];
  showAd: boolean;
  totalReviews: number;
  page: number;
};

export async function loader({ params, request }: LoaderFunctionArgs) {
  const timings = makeTimings("Full loader");
  let stationId = params.stationId;
  const url = new URL(request.url);
  const page = Number(url.searchParams.get("page")) || 1;
  const pageSize = 50;

  //cookies
  const userId = await getUserId(request);
  const isUserBot = isbot(request.headers.get("user-agent"));

  const cookieHeader = request.headers.get("Cookie");
  const cookie = (await locationViewsCookie.parse(cookieHeader)) || {};
  let showAd = false;

  if (cookie.views && !userId && !isUserBot) {
    if (cookie.views >= 2) {
      if (stationId) {
        return redirect(
          `/login?redirectTo=/estacoes/${stationId.toString()}&reason=limit`,
        );
      } else {
        return redirect(`/login?redirectTo=/estacoes/&reason=limit}`);
      }
    }
  }
  cookie.views = cookie.views + 1;

  if (stationId) {
    stationId = stationId.split("-").pop();
    const isAllNumbers = /^\d+$/.test(stationId!);
    if (!isAllNumbers) {
      stationId = stationId!.match(/\d+/g)?.join("");
    }
  } else {
    return json({ station: null, lastUserReviews: [], showAd });
  }

  // First get total count of reviews
  const totalReviews = await prisma.review.count({
    where: { locationId: Number(stationId) },
  });

  let station = await time(
    () =>
      prisma.location.findFirst({
        where: { id: Number(stationId) },
        select: {
          id: true,
          name: true,
          address: true,
          description: true,
          hours: true,
          parkingLevel: true,
          positives: true,
          negatives: true,
          averagePower: true,
          maxReportedPower: true,
          formattedPhoneNumber: true,
          costDescription: true,
          hasMap: true,
          url: true,
          city: true,
          state: true,
          country: true,
          majorityNetworkId: true,
          icon_type: true,
          objectKey: true,
          tags: {
            select: {
              name: true,
            },
          },
          reverseGeocodedAddressComponents: {
            select: {
              administrativeArea1: true,
              administrativeArea2: true,
            },
          },
          isInactive: true,
          stations: {
            select: {
              id: true,
              networkId: true,
              outlets: {
                select: {
                  id: true,
                  connectorName: true,
                  connector: true,
                  kilowatts: true,
                },
              },
            },
          },
          reviews: {
            select: {
              id: true,
              comment: true,
              rating: true,
              createdAt: true,
              kilowatts: true,
              vehicleName: true,
              vehicleMake: true,
              user: {
                select: {
                  id: true,
                  username: true,
                  score: true,
                  image: {
                    select: {
                      id: true,
                      objectKey: true
                    },
                  },
                },
              },
            },
            orderBy: {
              createdAt: "desc",
            },
            take: pageSize,
            skip: (page - 1) * pageSize,
          },
          stationImages: {
            select: {
              id: true,
              objectKey: true,
              createdAt: true,
              userId: true,
              user: {
                select: {
                  username: true,
                },
              },
            },
            orderBy: {
              createdAt: "desc",
            },
          },
        },
      }),
    { timings, type: "station loader" },
  );

  let lastUserReviews = await prisma.review.findMany({
    where: {
      locationId: Number(stationId),
      userId: userId as string,
    },
    orderBy: {
      createdAt: "desc",
    },
    select: {
      createdAt: true,
      comment: true,
      rating: true,
      kilowatts: true,
      vehicleName: true,
    },
  });

  const timeZone = "America/Sao_Paulo";
  station?.reviews.forEach((review: any) => {
    const utcDate = new Date(review.createdAt);
    const brazilDate = toZonedTime(utcDate, timeZone);
    review.createdAt = format(brazilDate, "dd/MM/yyyy HH:mm", { timeZone });
  });

  let formattedLastUserReviews = [] as any[];

  if (lastUserReviews.length > 0) {
    formattedLastUserReviews = lastUserReviews.map((review) => {
      try {
        const utcDate = new Date(review.createdAt);
        if (isNaN(utcDate.getTime())) {
          console.error("Invalid date:", review.createdAt);
          return {
            ...review,
            formattedDate: "Data inválida",
          };
        }

        const brazilDate = toZonedTime(utcDate, timeZone);
        return {
          ...review,
          formattedDate: format(brazilDate, "d 'de' MMMM 'de' yyyy", {
            locale: ptBR,
            timeZone,
          }),
        };
      } catch (error) {
        console.error("Error formatting date:", error, review.createdAt);
        return {
          ...review,
          formattedDate: "Data inválida",
        };
      }
    });
  }

  // Fetch all thread messages for all reviews in a single query
  let threadMessages = [];
  if (station && station.reviews && station.reviews.length > 0) {
    // Extract all review IDs
    const reviewIds = station.reviews.map((review: any) => review.id);

    // Fetch all thread messages for these reviews in a single query
    threadMessages = await prisma.threadMessage.findMany({
      where: {
        reviewId: {
          in: reviewIds,
        },
      },
      orderBy: {
        createdAt: "asc",
      },
      select: {
        id: true,
        message: true,
        createdAt: true,
        reviewId: true,
        user: {
          select: {
            username: true,
            image: {
              select: {
                id: true,
                objectKey: true
              },
            },
          },
        },
      },
    });

    // Format dates for thread messages
    threadMessages = threadMessages.map((message) => ({
      ...message,
      createdAt: format(
        toZonedTime(new Date(message.createdAt), timeZone),
        "dd/MM/yyyy HH:mm",
        { timeZone },
      ),
    }));

    // Group thread messages by reviewId for easier access
    const threadMessagesByReviewId = threadMessages.reduce(
      (acc: Record<number, any[]>, message: any) => {
        if (!acc[message.reviewId]) {
          acc[message.reviewId] = [];
        }
        acc[message.reviewId].push(message);
        return acc;
      },
      {},
    );

    // Add count and messages to each review
    station.reviews.forEach((review: any) => {
      const messagesForReview = threadMessagesByReviewId[review.id] || [];
      review.threadMessages = messagesForReview;
      review.threadMessageCount = messagesForReview.length;
    });
  }

  return json(
    {
      station,
      lastUserReviews: formattedLastUserReviews,
      showAd,
      totalReviews,
      page,
    },
    {
      headers: {
        "Cache-Control": "private, max-age=3600",
        Vary: "Cookie",
        "Server-Timing": timings.toString(),
        "Set-Cookie": await locationViewsCookie.serialize(cookie),
      },
    },
  );
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const location = data?.station ?? null;

  if (!location) {
    return [
      {
        title: "Carregados",
        description: "Carregados",
      },
    ];
  }

  const title = `Estação ${location.name}`
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  const description = `Estação de carregamento para veículos elétricos ${
    location.name
  } em ${location.city} - ${formatState(location.state).replaceAll(" ()", "")}`
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  const currentUrl = `https://carregados.com.br/estacoes/${location.url}${location.id}`;

  return [
    { title: title },
    {
      name: "description",
      content: description,
    },
    {
      itemProp: "name",
      content: title,
    },
    {
      itemProp: "description",
      content: description,
    },
    { itemProp: "image", content: "https://carregados.com.br/img/carregados-og.jpg" },
    { property: "og:url", content: currentUrl },
    { property: "og:type", content: "website" },
    {
      property: "og:title",
      content: title,
    },
    {
      property: "og:description",
      content: description,
    },
    {
      property: "og:image",
      content: "https://carregados.com.br/img/carregados-og.jpg",
    },
    { name: "twitter:card", content: "summary_large_image" },
    {
      name: "twitter:title",
      content: title,
    },
    {
      name: "twitter:description",
      content: description,
    },
    {
      name: "twitter:image",
      content: "https://carregados.com.br/img/carregados-og.jpg",
    },
    {
      name: "twitter:image:alt",
      content: title,
    },
    {
      tagName: "link",
      rel: "canonical",
      href: currentUrl,
    },
    {
      "script:ld+json": {
        "@type": "LocalBusiness",
        "@context": "http://schema.org",
        name: location.name,
        description: location.description,
        address: location.address,
        image: location.stationImages[0]?.id
          ? `https://carregados.com.br${getStationImgSrc(location.stationImages[0].id)}`
          : "",
        url: currentUrl,
        publicAccess: true,
        ...(location.positives + location.negatives > 0 && {
          aggregateRating: {
            "@type": "AggregateRating",
            bestRating: 10,
            worstRating: 0,
            ratingValue: Math.round(
              (location.positives / (location.positives + location.negatives)) *
                10,
            ),
            reviewCount: location.positives + location.negatives,
          },
        }),
      },
    },
  ];
};

export default function StationId() {
  const { station, lastUserReviews, totalReviews, showAd, page } =
    useLoaderData<LoaderData>() as LoaderData;
  const fetcher = useFetcher<ReviewActionData>();
  const imageFetcher = useFetcher();
  const emailFetcher = useFetcher();
  const favoriteFetcher = useFetcher();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState(true);
  const user = useOptionalUser();
  const formRef = React.useRef<HTMLFormElement>(null);
  const imageFormRef = React.useRef<HTMLFormElement>(null);

  React.useEffect(() => {
    const reviewData = fetcher.data;
    // console.log('Review Fetcher Data:', reviewData); // Removed debug log

    if (
      reviewData &&
      reviewData.status === 200 &&
      reviewData.rating !== "2" // Only toast for positive/negative
    ) {
      // console.log('Review Success Toast Condition Met'); // Removed debug log
      toast.success("Check-in Enviado Com Sucesso 🎉", {
        description: "⭐ Você ganhou +2 pontos!",
      });
      // Reset form state if submission was successful
      if (formRef.current) {
        formRef.current.reset();
      }
    }
    // Handle potential error messages from fetcher
    if (reviewData && reviewData.status === 500 && reviewData.message) {
      // console.log('Review Error Toast Condition Met'); // Removed debug log
      toast.error("Erro ao enviar avaliação", {
        description: reviewData.message,
      });
    }
  }, [fetcher.data]);

  const favorites = user?.favoriteStations.map((favorite) => {
    return favorite.locationId;
  });

  const renderStationContent = (isSheet: boolean) => {
    if (!station) return null;
    return (
      <div className="flex w-full max-w-full flex-col space-y-4">
        <StationHeader station={station} isSheet={isSheet} />

        <div>
          <div>
            <div className="flex flex-col space-y-4 px-2">
              {showAd && <AdPlaceholder />}
              {user && lastUserReviews && lastUserReviews.length > 0 && (
                <LastInteraction lastUserReviews={lastUserReviews} />
              )}
              <Ratings
                totalRatings={station.positives + station.negatives}
                positiveRatings={station.positives}
                negativeRatings={station.negatives}
                tags={station.tags}
              />
              {station.averagePower && station.maxReportedPower ? (
                <Speed
                  averageSpeed={station.averagePower}
                  maxSpeed={station.maxReportedPower}
                />
              ) : null}

              <ReviewsSection
                stationId={station.id}
                stationUrl={station.url}
                initialReviews={station.reviews || []}
                totalReviews={totalReviews}
                user={user}
                reviewFetcher={fetcher}
                formRef={formRef}
              />
              <ImageGallery
                stationId={station.id}
                stationName={station.name}
                stationUrl={station.url}
                stationImages={station.stationImages}
                user={user}
                imageFetcher={imageFetcher}
                imageFormRef={imageFormRef}
                isSheet={isSheet}
              />
              <ConnectorList stations={station.stations} />
            </div>
          </div>
        </div>

        <div className="space-y-4 px-2">
          <StationDescription description={station.description} />
          <StationInfo station={station} />
        </div>

        <div className="space-y-4 px-2 pb-4">
          <ActionButtons
            station={station}
            user={user}
            favorites={favorites}
            favoriteFetcher={favoriteFetcher}
            emailFetcher={emailFetcher}
          />
        </div>
      </div>
    );
  };

  if (location.state?.history) {
    return (
      <Sheet
        open={open}
        onOpenChange={() => {
          setOpen(!open);
          navigate(location.state.history, { preventScrollReset: true });
        }}
      >
        <SheetContent
          className="w-full overflow-auto bg-gradient-to-b from-stone-50 to-gray-50 p-0 dark:bg-background dark:from-background dark:to-background"
          side={"left"}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Estação {station?.name}</SheetTitle>
          </SheetHeader>
          {/* Render content using helper function */}
          <div>{renderStationContent(true)}</div>
          <SheetFooter>
            <SheetClose className="mb-6 flex" asChild>
              <Button
                className={`w-full rounded-none px-2`}
                variant={"destructive"}
              >
                Fechar
              </Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    );
  } else {
    return (
      <main className="mx-auto max-w-4xl bg-background py-10 md:rounded-lg md:border md:border-border md:p-6 md:shadow-lg">
        {station &&
          station.city &&
          station.state &&
          station.country === "brasil" && (
            <nav className="mb-2 ml-3">
              <ul className="flex items-center space-x-1 text-sm">
                <li className="flex items-center">
                  <Link
                    reloadDocument
                    to="/estacoes"
                    className="text-muted-foreground underline underline-offset-2 transition-colors hover:text-primary"
                  >
                    Brasil
                  </Link>
                  <ChevronRight className="mx-1 h-4 w-4 text-muted-foreground" />
                  <Link
                    reloadDocument
                    to={`/estacoes?estado=${encodeURIComponent(station.state)}`}
                    className="capitalize text-muted-foreground underline underline-offset-2 transition-colors hover:text-primary"
                  >
                    {formatState(station.state).replaceAll(" ()", "")}
                  </Link>
                  <ChevronRight className="mx-1 h-4 w-4 text-muted-foreground" />
                  <Link
                    reloadDocument
                    to={`/estacoes?cidade=${encodeURIComponent(station.city)}&estado=${encodeURIComponent(station.state)}`}
                    className="capitalize text-muted-foreground underline underline-offset-2 transition-colors hover:text-primary"
                  >
                    {station.city}
                  </Link>
                  <ChevronRight className="mx-1 h-4 w-4 text-muted-foreground" />
                  <span className="hidden text-muted-foreground md:block">
                    {station.name}
                  </span>
                </li>
              </ul>
            </nav>
          )}
        {/* Render content using helper function */}
        {renderStationContent(false)}
        {station?.city && station?.state && station?.country === "brasil" && (
          <Button
            className="animate-gradient-x w-full rounded-none bg-gradient-to-r from-purple-600 via-pink-500 to-yellow-500 capitalize text-white hover:text-gray-200 hover:opacity-80 md:rounded-lg"
            variant="outline"
          >
            <Link
              reloadDocument
              className="flex w-full items-center justify-center font-semibold"
              to={`/estacoes?cidade=${encodeURIComponent(
                station.city,
              )}&estado=${encodeURIComponent(station.state)}`}
            >
              Ver todas as estações em {station.city}
            </Link>
          </Button>
        )}
      </main>
    );
  }
}

export const handle: SEOHandle = {
  getSitemapEntries: serverOnly$(async (request) => {
    const locations = await prisma.location.findMany({
      select: {
        id: true,
        updatedAt: true,
        url: true,
      },
      where: {
        country: "brasil",
        city: {
          not: null,
        },
      },
    });

    return locations.map((location) => {
      return {
        //@ts-ignore
        route: `/estacoes/${encodeURIComponent(location.url + location.id)}`,
        priority: 0.7,
        changefreq: "monthly",
        //@ts-ignore
        lastmod: location.updatedAt.toISOString(),
      };
    });
  }),
};

export function ErrorBoundary() {
  return <GeneralErrorBoundary />;
}
